<template>
  <div id="CenterSettings" class="h-screen w-screen flex flex-col overflow-hidden relative center-bg"
    :class="{ 'xl:h-auto xl:w-auto xl:overflow-visible': isDesktop }"
    style="font-family: 'Lato', sans-serif;">

    <!-- Decorative background (mobile only) -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none xl:hidden">
      <div class="absolute -top-20 -right-8 w-56 h-[380px] rotate-[20deg] rounded-[36px] center-deco-top"></div>
      <div class="absolute -bottom-16 -left-12 w-72 h-72 rounded-full center-deco-bottom"></div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 relative z-10" :class="{ 'xl:flex-none': isDesktop }">

      <!-- Header: back + page title (mobile only) -->
      <div v-if="!isDesktop" class="pt-safe flex items-center gap-3 px-safe pb-3 center-header" :class="{ scrolled: isScrolled }">
        <button @click="backTo({ name: 'Profile' })"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card center-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">{{ $t('profile.centers.page_title') }}</p>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 min-h-0 overflow-y-auto pb-safe px-safe-flush center-scroll" :class="{ 'xl:flex-none xl:overflow-visible xl:pb-0 xl:px-0': isDesktop }" @scroll="onScroll">
        <!-- Compact title -->
        <div class="flex items-center gap-3 mt-4 mb-5 px-5">
          <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0 center-title-icon">
            <AdjustmentsVerticalIcon class="h-[17px] w-[17px]" style="color: #06C8FF;" />
          </div>
          <h1 class="font-bold text-[18px] tracking-tight leading-snug text-foreground">
            {{ center?.title }}
          </h1>
        </div>

        <template v-if="center">
          <!-- Display name -->
          <div class="px-5 mb-4">
            <div class="flex items-center gap-2 mb-2 pl-1">
              <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #FF9E1C, #E07A00);"></div>
              <h2 class="text-[13px] font-semibold text-muted-foreground">
                {{ $t('profile.centers.display_name') }}
              </h2>
            </div>
            <div class="center-card rounded-xl overflow-hidden px-4 py-4">
              <p class="text-[12px] text-muted-foreground leading-snug mb-3">
                {{ $t('profile.centers.display_name_description') }}
              </p>
              <InputText v-model="center.preferences.label" name="label" />
            </div>
          </div>

          <!-- Visibility toggles -->
          <div class="px-5 mb-4">
            <div class="flex items-center gap-2 mb-2 pl-1">
              <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #FF9E1C, #E07A00);"></div>
              <h2 class="text-[13px] font-semibold text-muted-foreground">
                {{ $t('profile.centers.visibility') }}
              </h2>
            </div>

            <!-- Single center info banner -->
            <div v-if="isSingleCenter" class="center-info-banner flex items-start gap-2.5 rounded-xl px-3.5 py-3 mb-2.5">
              <InformationCircleIcon class="h-4 w-4 shrink-0 mt-px" style="color: #06C8FF;" />
              <p class="text-[11.5px] leading-snug text-muted-foreground">
                {{ $t('profile.centers.single_center_hint') }}
              </p>
            </div>

            <div class="flex flex-col gap-2.5">
              <div class="center-card rounded-xl overflow-hidden px-4 py-4">
                <InputToggle v-model="center.preferences.display" name="active_center"
                  position="right"
                  :show-indicator="false"
                  :disabled="isSingleCenter"
                  :label="$t('profile.centers.display_center')"
                  :description="$t('profile.centers.display_center_description')" />
              </div>
              <div class="center-card rounded-xl overflow-hidden px-4 py-4">
                <InputToggle v-model="center.preferences.in_dashboard" name="in_dashboard"
                  position="right"
                  :show-indicator="false"
                  :disabled="isSingleCenter"
                  :label="$t('profile.centers.include_in_dashboard')"
                  :description="$t('profile.centers.include_in_dashboard_description')" />
              </div>
            </div>
          </div>

          <!-- Danger zone -->
          <div class="mx-5 mt-6 mb-5 center-separator"></div>
          <div class="px-5 flex justify-center">
            <button @click="confirmDeleteIsOpen = true"
              class="center-delete-pill flex items-center gap-2 px-5 h-10 rounded-xl text-[13px] font-semibold active:scale-[0.97] transition-all duration-150">
              <TrashIcon class="h-3.5 w-3.5 stroke-[2.5]" />
              {{ $t('profile.centers.delete') }}
            </button>
          </div>
        </template>

      </div>
    </div>

    <!-- Delete confirmation dialog -->
    <FormDialog :isOpen="confirmDeleteIsOpen" :title="$t('profile.centers.delete')"
      :sending="sendingDeletion" :confirm-text="$t('profile.centers.delete_confirm_label')"
      :closeText="$t('profile.centers.delete_cancel_label')" color="danger" @close="closeConfirmDelete"
      @submit-form="confirmDelete">
      <i18n-t keypath="profile.centers.delete_confirm_info" tag="p">
        <template #name><span class="font-bold text-foreground">{{ center?.title }}</span></template>
      </i18n-t>
    </FormDialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch, watchEffect } from 'vue';
import { useUserManagerStore } from '../../stores/userManagerStore';
import { useProfileStore } from '../../stores/profileStore';
import { useSnackbarStore } from '../../stores/snackbarStore';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, AdjustmentsVerticalIcon, TrashIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
import FormDialog from '../../components/dialogs/FormDialog.vue';
import InputText from '../../components/form/input/InputText.vue';
import InputToggle from '../../components/form/input/InputToggle.vue';
import { useAppRouter } from '../../composables/useAppRouter';
import { useIsDesktop } from '../../composables/useIsDesktop';

const { backTo } = useAppRouter();
const isDesktop = useIsDesktop();
const { t } = useI18n();
const userManagerStore = useUserManagerStore();
const profileStore = useProfileStore();
const snackbarStore = useSnackbarStore();
const router = useRouter();

const isScrolled = ref(false);
function onScroll(e: Event) {
  isScrolled.value = (e.target as HTMLElement).scrollTop > 8;
}

const isSingleCenter = computed(() => (userManagerStore.user?.centers.length ?? 0) <= 1);

const centerId = computed(() => router.currentRoute.value.params.id as string);

// Save previous center's preferences when switching centers
watch(centerId, async (_newId, _oldId) => {
  if (_oldId) await save();
});

const center = computed(() => {
  const c = userManagerStore.user?.centers.find(c => c.id == parseInt(centerId.value));
  // Ensure preferences is never null — init with sensible defaults
  if (c && !c.preferences) {
    c.preferences = { center_id: c.id, label: c.title, display: true, in_dashboard: true };
  }
  return c;
});

// Force display & in_dashboard to true when there is only one center
// (prevents edge case where API returns false on a single center)
watchEffect(() => {
  if (isSingleCenter.value && center.value?.preferences) {
    center.value.preferences.display = true;
    center.value.preferences.in_dashboard = true;
  }
});

const save = async () => {
  if (center.value) {
    const success = await profileStore.saveCenterUserPreferences(center.value);
    if (!success) {
      snackbarStore.addSnackbar(t('profile.centers.save_error'), 'error');
    }
  }
};

const confirmDeleteIsOpen = ref(false);
const sendingDeletion = ref(false);
const closeConfirmDelete = () => {
  confirmDeleteIsOpen.value = false;
};
const confirmDelete = async () => {
  sendingDeletion.value = true;
  const success = await profileStore.deleteCenter(parseInt(centerId.value));
  sendingDeletion.value = false;

  if (success) {
    userManagerStore.removeCenter(parseInt(centerId.value));
    confirmDeleteIsOpen.value = false;
    router.push({ name: 'Profile' });
  }
};

onUnmounted(async () => {
  await save();
});
</script>

<style scoped>
/* Light mode */
.center-header {
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, backdrop-filter 0.2s ease;
}
.center-header.scrolled {
  border-bottom-color: rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.center-bg {
  background: linear-gradient(175deg, #FFFFFF 0%, #F0F7FA 40%, #F4F4F4 100%);
}
@media (min-width: 1280px) {
  .center-bg { background: transparent; }
}
.center-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.09) 0%, transparent 55%);
}
.center-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.06) 0%, transparent 60%);
}
.center-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.center-card {
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.center-title-icon {
  background: linear-gradient(145deg, rgba(6, 200, 255, 0.10), rgba(6, 200, 255, 0.03));
  border: 1px solid rgba(6, 200, 255, 0.12);
}
.center-delete-pill {
  color: #D42B26;
  background: rgba(227, 33, 28, 0.06);
  border: 1px solid rgba(227, 33, 28, 0.10);
}
.center-delete-pill:active {
  background: rgba(227, 33, 28, 0.12);
}
.center-info-banner {
  background: rgba(6, 200, 255, 0.05);
  border: 1px solid rgba(6, 200, 255, 0.12);
}
.center-separator {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
}
.center-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.center-scroll::-webkit-scrollbar {
  display: none;
}

/* Dark mode */
:is(.dark *).center-header.scrolled {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).center-bg {
  background: linear-gradient(175deg, #1B1C1E 0%, #1e2528 40%, #1B1C1E 100%);
}
@media (min-width: 1280px) {
  :is(.dark *).center-bg { background: transparent; }
}
:is(.dark *).center-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.15) 0%, transparent 55%);
}
:is(.dark *).center-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.04) 0%, transparent 50%);
}
:is(.dark *).center-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
:is(.dark *).center-card {
  background: #1E2024;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
:is(.dark *).center-title-icon {
  background: linear-gradient(145deg, rgba(6, 200, 255, 0.08), rgba(6, 200, 255, 0.02));
  border: 1px solid rgba(6, 200, 255, 0.10);
}
:is(.dark *).center-delete-pill {
  color: #F0605C;
  background: rgba(227, 33, 28, 0.10);
  border: 1px solid rgba(227, 33, 28, 0.16);
}
:is(.dark *).center-delete-pill:active {
  background: rgba(227, 33, 28, 0.18);
}
:is(.dark *).center-separator {
  background: rgba(255, 255, 255, 0.06);
}
:is(.dark *).center-info-banner {
  background: rgba(6, 200, 255, 0.04);
  border: 1px solid rgba(6, 200, 255, 0.10);
}
</style>
